/**
 * <fullscreen-menu-component>
 *
 * A full-screen video panel that animates open with a circular "iris" reveal
 * from whichever trigger button was clicked (mobile or desktop). Unlike a
 * native <dialog>, this is intentionally NOT a top-layer modal: the real
 * site header (logo, cart, search...) is kept visible and interactive above
 * it via z-index, so nothing needs to be duplicated inside the panel.
 */
class FullscreenMenuComponent extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLElement | null} */
    this.sheet = this.querySelector('.fsm__sheet');
    this.video = this.querySelector('video');

    if (!this.sheet) return;

    this._isOpen = false;

    this.triggers = Array.from(document.querySelectorAll(`[data-fsm-target="${this.id}"]`));
    this.closers = Array.from(this.querySelectorAll('[data-fsm-close]'));

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(trigger);
      });
    });

    this.closers.forEach((closer) => {
      closer.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });

    this._boundKeydown = (event) => {
      if (event.key === 'Escape' && this._isOpen) this.close();
    };
    document.addEventListener('keydown', this._boundKeydown);

    this._boundResize = () => {
      if (this._isOpen) this._setOriginFromTrigger(this._lastTrigger);
    };
    window.addEventListener('resize', this._boundResize);

    // Closing on cart click is a nice-to-have, not core behavior: the cart
    // trigger lives in the header, which is always visible and clickable
    // above this panel, so this never blocks anyone from opening the cart.
    this._boundCartClick = (event) => {
      if (!this._isOpen) return;
      if (event.target.closest('[aria-controls="cart-drawer"], .action__cart')) {
        this.close();
      }
    };
    document.addEventListener('click', this._boundCartClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._boundKeydown);
    document.removeEventListener('click', this._boundCartClick);
    window.removeEventListener('resize', this._boundResize);
  }

  toggle(trigger) {
    if (this._isOpen) {
      this.close();
    } else {
      this.open(trigger);
    }
  }

  _setOriginFromTrigger(trigger) {
    const rect = trigger ? trigger.getBoundingClientRect() : null;
    // clip-path coordinates are relative to the sheet's own box, which now
    // starts below the header rather than at the viewport's top edge — so we
    // offset by the sheet's own position, not the raw viewport coordinates.
    const sheetRect = this.sheet.getBoundingClientRect();
    const x = rect ? rect.left + rect.width / 2 - sheetRect.left : sheetRect.width;
    const y = rect ? rect.top + rect.height / 2 - sheetRect.top : 0;
    this.style.setProperty('--fsm-origin-x', `${x}px`);
    this.style.setProperty('--fsm-origin-y', `${y}px`);
  }

  open(trigger) {
    if (this._isOpen) return;

    this._lastTrigger = trigger || this.triggers[0];

    // Unhide first so the sheet has real layout dimensions to measure —
    // it stays invisible regardless, since the base CSS clip-path is 0%.
    this.sheet.hidden = false;
    this._setOriginFromTrigger(this._lastTrigger);

    document.documentElement.classList.add('fsm-lock-scroll');

    // Wait a frame so the browser paints the closed (0%) clip-path first,
    // otherwise the transition to the open state won't animate.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.sheet.classList.add('fsm__sheet--open');
      });
    });

    if (this.video) {
      this.video.play().catch(() => {});
    }

    this._isOpen = true;
    this.setAttribute('open', '');
    this._setTriggersExpanded(true);
  }

  close() {
    if (!this._isOpen) return;

    const sheet = this.sheet;
    sheet.classList.remove('fsm__sheet--open');

    const finish = () => {
      sheet.removeEventListener('transitionend', onTransitionEnd);
      sheet.hidden = true;
      document.documentElement.classList.remove('fsm-lock-scroll');
      this._isOpen = false;
      this.removeAttribute('open');
      this._setTriggersExpanded(false);
      if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
        this._lastTrigger.focus({ preventScroll: true });
      }
    };

    const onTransitionEnd = (event) => {
      if (event.target === sheet && event.propertyName === 'clip-path') finish();
    };

    sheet.addEventListener('transitionend', onTransitionEnd);
    // Safety net in case the transition event never fires (e.g. reduced motion).
    window.setTimeout(finish, 900);
  }

  _setTriggersExpanded(isOpen) {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(isOpen));
    });
  }
}

if (!customElements.get('fullscreen-menu-component')) {
  customElements.define('fullscreen-menu-component', FullscreenMenuComponent);
}